import * as styles from "./YearPickerGrid.css";

import type { JSX, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { useLingui } from "@lingui/react/macro";

import { useYearPickerContext } from "./YearPicker.context";
import { YearPickerYear } from "./YearPickerYear";
import { YEARS_PER_ROW, YEARS_PER_VIEW } from "./constants";
import { decadeOf, thisYear } from "./yearMath";

function defaultActiveYear(viewDecade: number, selected: number | null) {
  if (null !== selected && decadeOf(selected) === viewDecade) {
    return selected;
  }

  const now = thisYear();

  return decadeOf(now) === viewDecade ? now : viewDecade;
}

export function YearPickerGrid(): JSX.Element {
  const { t } = useLingui();

  const { viewDecade, selected, min, max, goToView } = useYearPickerContext();

  const gridRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef<number | null>(null);

  const [activeYear, setActiveYear] = useState(() =>
    defaultActiveYear(viewDecade, selected),
  );

  useEffect(() => {
    if (null !== pendingFocusRef.current) {
      const pending = pendingFocusRef.current;

      pendingFocusRef.current = null;
      setActiveYear(pending);
      focusYear(pending);

      return;
    }

    setActiveYear(defaultActiveYear(viewDecade, selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDecade]);

  function focusYear(year: number) {
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-year="${year}"]`)
      ?.focus({ preventScroll: true });
  }

  function navigateTo(year: number) {
    if (decadeOf(year) === viewDecade) {
      setActiveYear(year);
      focusYear(year);

      return;
    }

    pendingFocusRef.current = year;
    goToView(year);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case "ArrowLeft":
        navigateTo(activeYear - 1);
        break;
      case "ArrowRight":
        navigateTo(activeYear + 1);
        break;
      case "ArrowUp":
        navigateTo(activeYear - YEARS_PER_ROW);
        break;
      case "ArrowDown":
        navigateTo(activeYear + YEARS_PER_ROW);
        break;
      case "Home":
        navigateTo(decadeOf(activeYear));
        break;
      case "End":
        navigateTo(decadeOf(activeYear) + 9);
        break;
      case "PageUp":
        navigateTo(activeYear - 10);
        break;
      case "PageDown":
        navigateTo(activeYear + 10);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  const now = thisYear();

  const cells = Array.from(
    { length: YEARS_PER_VIEW },
    (_, index) => viewDecade - 1 + index,
  );

  return (
    <div
      ref={gridRef}
      role="grid"
      aria-label={t`Years`}
      className={styles.grid}
      onKeyDown={handleKeyDown}
    >
      {Array.from({ length: YEARS_PER_VIEW / YEARS_PER_ROW }, (_, row) => (
        <div key={row} role="row" className={styles.row}>
          {cells
            .slice(row * YEARS_PER_ROW, row * YEARS_PER_ROW + YEARS_PER_ROW)
            .map((year) => (
              <YearPickerYear
                key={year}
                year={year}
                selected={year === selected}
                current={year === now}
                active={year === activeYear}
                outside={decadeOf(year) !== viewDecade}
                disabled={
                  (null !== min && year < min) || (null !== max && year > max)
                }
              />
            ))}
        </div>
      ))}
    </div>
  );
}
